/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Observable, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { Action, ActionCreators } from './actions';
import { RootState } from '../index';
import { handleErrorInterceptor } from '../../utils/AuthUtils';

// get network options
const requestNetworkOptionsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedNetworkOptionsSuccess.create(response);
};

const requestNetworkOptionsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedNetworkOptionsError.create(error);
};

export const requestNetworkOptionsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestNetworkOptions.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/options/networks`).pipe(
          map((response: AjaxResponse<any>): Action => requestNetworkOptionsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestNetworkOptionsError(error))),
          ),
        ),
    ),
  );

// get terminal type options
const requestTerminalTypeOptionsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedTerminalTypeOptionsSuccess.create(response);
};

const requestTerminalTypeOptionsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedTerminalTypeOptionsError.create(error);
};

export const requestTerminalTypeOptionsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestTerminalTypeOptions.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/options/terminal-types`).pipe(
          map((response: AjaxResponse<any>): Action => requestTerminalTypeOptionsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestTerminalTypeOptionsError(error))),
          ),
        ),
    ),
  );

// get platform type options
const requestPlatformTypeOptionsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedPlatformTypeOptionsSuccess.create(response);
};

const requestPlatformTypeOptionsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedPlatformTypeOptionsError.create(error);
};

export const requestPlatformTypeOptionsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestPlatformTypeOptions.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/options/platform-types`).pipe(
          map((response: AjaxResponse<any>): Action => requestPlatformTypeOptionsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestPlatformTypeOptionsError(error))),
          ),
        ),
    ),
  );

// get datapath label options
const requestDatapathLabelOptionsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedDatapathLabelOptionsSuccess.create(response);
};

const requestDatapathLabelOptionsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedDatapathLabelOptionsError.create(error);
};

export const requestDatapathLabelOptionsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestDatapathLabelOptions.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/options/datapath-labels`).pipe(
          map((response: AjaxResponse<any>): Action => requestDatapathLabelOptionsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestDatapathLabelOptionsError(error))),
          ),
        ),
    ),
  );

// get customer group options
const requestCustomerGroupOptionsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedCustomerGroupOptionsSuccess.create(response);
};

const requestCustomerGroupOptionsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedCustomerGroupOptionsError.create(error);
};

export const requestCustomerGroupOptionsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestCustomerGroupOptions.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/options/customer-groups`).pipe(
          map((response: AjaxResponse<any>): Action => requestCustomerGroupOptionsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestCustomerGroupOptionsError(error))),
          ),
        ),
    ),
  );

// get antenna options
const requestAntennaOptionsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedAntennaOptionsSuccess.create(response);
};

const requestAntennaOptionsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedAntennaOptionsError.create(error);
};

export const requestAntennaOptionsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestAntennaOptions.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/options/antennas`).pipe(
          map((response: AjaxResponse<any>): Action => requestAntennaOptionsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestAntennaOptionsError(error))),
          ),
        ),
    ),
  );

// get modem type options
const requestModemTypeOptionsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedModemTypeOptionsSuccess.create(response);
};

const requestModemTypeOptionsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedModemTypeOptionsError.create(error);
};

export const requestModemTypeOptionsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestModemTypeOptions.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/options/modem-types`).pipe(
          map((response: AjaxResponse<any>): Action => requestModemTypeOptionsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestModemTypeOptionsError(error))),
          ),
        ),
    ),
  );